/**
 * Admin Health & Operations Tab Component
 * 
 * Displays worker health status and operations metrics in a unified view.
 */

import { useQuery } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { getWorkerHealth, type WorkerHealthResponse, type StreamHealthStatus } from "@app/services/adminWorkerHealthApi";
import { getOpsMetrics, type OpsMetricsResponse } from "@app/services/adminOpsMetricsApi";
import "./admin-health-ops.css";

const WORKER_HEALTH_QUERY_KEY = ["admin", "worker-health"];
const OPS_METRICS_QUERY_KEY = ["admin", "ops-metrics"];

export function AdminHealthOpsTab(): JSX.Element {
  const { data: workerHealth, isLoading: healthLoading, isError: healthError, error: healthErrorDetails } = useQuery<WorkerHealthResponse>({
    queryKey: WORKER_HEALTH_QUERY_KEY,
    queryFn: getWorkerHealth,
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid hanging
    refetchOnMount: true, // Always fetch on mount
    staleTime: 10000, // Data is fresh for 10 seconds
    gcTime: 300000, // Cache for 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  const { data: opsMetrics, isLoading: metricsLoading, isError: metricsError, error: metricsErrorDetails } = useQuery<OpsMetricsResponse>({
    queryKey: OPS_METRICS_QUERY_KEY,
    queryFn: getOpsMetrics,
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid hanging
    refetchOnMount: true, // Always fetch on mount
    staleTime: 10000, // Data is fresh for 10 seconds
    gcTime: 300000, // Cache for 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  if (healthLoading || metricsLoading) {
    return (
      <div className="admin-health-ops-root">
        <Card title="Health & Operations">
          <div className="admin-health-ops-loading">Loading health and operations data...</div>
        </Card>
      </div>
    );
  }

  if (healthError || metricsError) {
    // Extract error messages with better handling for timeout/network errors
    const getErrorMessage = (error: unknown): string => {
      if (error instanceof Error) {
        if (error.message.includes("timeout") || error.message.includes("Timeout")) {
          return "Request timed out. The server may be slow or unavailable.";
        }
        if (error.message.includes("Network Error") || error.message.includes("network")) {
          return "Network error. Please check your connection.";
        }
        return error.message;
      }
      return String(error);
    };

    const healthErrorMsg = healthErrorDetails ? getErrorMessage(healthErrorDetails) : null;
    const metricsErrorMsg = metricsErrorDetails ? getErrorMessage(metricsErrorDetails) : null;
    
    const errorMessage = healthErrorMsg && metricsErrorMsg
      ? `Worker Health: ${healthErrorMsg} | Operations Metrics: ${metricsErrorMsg}`
      : healthErrorMsg
      ? `Worker Health: ${healthErrorMsg}`
      : metricsErrorMsg
      ? `Operations Metrics: ${metricsErrorMsg}`
      : "Failed to load health and operations data.";
    
    return (
      <div className="admin-health-ops-root">
        <Card title="Health & Operations">
          <div className="admin-health-ops-error">
            {errorMessage}
            <br />
            <small style={{ color: "var(--text-secondary)", fontSize: "0.875rem" }}>
              Please check the browser console for more details. The page will retry automatically.
            </small>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-health-ops-root">
      <Card
        title="Health & Operations"
        subtitle={`Last updated: ${new Date(opsMetrics?.timestamp || Date.now()).toLocaleString()}`}
      >
        <div className="admin-health-ops-grid">
          {/* Worker Health Section */}
          <div className="admin-health-ops-section">
            <h3>Worker Health</h3>
            {workerHealth ? (
              <div className="admin-health-ops-streams">
                {["entry", "exit", "monitor"].map((streamType) => {
                  const stream = workerHealth[streamType as keyof WorkerHealthResponse] as StreamHealthStatus | undefined;
                  if (!stream) {
                    return null;
                  }
                  return (
                    <div key={streamType} className="admin-health-ops-stream">
                      <h4>{stream.stream_type || streamType.toUpperCase()}</h4>
                      <div className="admin-health-ops-metrics">
                        <div className="admin-health-ops-metric">
                          <span className="admin-health-ops-metric__label">Pending</span>
                          <span className={`admin-health-ops-metric__value ${stream.pending_count > 0 ? "warning" : ""}`}>
                            {stream.pending_count ?? 0}
                          </span>
                        </div>
                        <div className="admin-health-ops-metric">
                          <span className="admin-health-ops-metric__label">Stream Length</span>
                          <span className="admin-health-ops-metric__value">{stream.stream_length ?? 0}</span>
                        </div>
                        <div className="admin-health-ops-metric">
                          <span className="admin-health-ops-metric__label">Last Processed</span>
                          <span className="admin-health-ops-metric__value">
                            {stream.last_processed || "never"}
                          </span>
                        </div>
                        <div className="admin-health-ops-metric">
                          <span className="admin-health-ops-metric__label">Stalled</span>
                          <span className={`admin-health-ops-metric__value ${stream.is_stalled ? "error" : "success"}`}>
                            {stream.is_stalled ? "Yes" : "No"}
                          </span>
                        </div>
                        <div className="admin-health-ops-metric">
                          <span className="admin-health-ops-metric__label">Recovery Attempts</span>
                          <span className="admin-health-ops-metric__value">{stream.recovery_attempts ?? 0}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="admin-health-ops-error" style={{ padding: "1rem", fontSize: "0.875rem" }}>
                Worker health data not available. Please wait for the next refresh.
              </div>
            )}
          </div>

          {/* Operations Metrics Section */}
          <div className="admin-health-ops-section">
            <h3>Operations Metrics</h3>
            {opsMetrics && opsMetrics.queue_metrics ? (
              <div className="admin-health-ops-metrics-grid">
                <div className="admin-health-ops-card">
                  <h4>Queue Lag</h4>
                  {Object.keys(opsMetrics.queue_metrics).length > 0 ? (
                    Object.entries(opsMetrics.queue_metrics).map(([streamType, metrics]) => (
                      <div key={streamType} className="admin-health-ops-metric-row">
                        <span>{streamType.toUpperCase()}:</span>
                        <span>Lag: {metrics?.consumer_lag ?? 0}, Pending: {metrics?.pending_count ?? 0}</span>
                      </div>
                    ))
                  ) : (
                    <div className="admin-health-ops-metric-row">No queue metrics available</div>
                  )}
                </div>
                <div className="admin-health-ops-card">
                  <h4>Reconciliation</h4>
                  <div className="admin-health-ops-metric-row">
                    <span>Corrections Total:</span>
                    <span>{opsMetrics.reconciliation_metrics?.corrections_total ?? 0}</span>
                  </div>
                </div>
                <div className="admin-health-ops-card">
                  <h4>Orphan Linking</h4>
                  <div className="admin-health-ops-metric-row">
                    <span>Created:</span>
                    <span>{opsMetrics.orphan_metrics?.links_created_total ?? 0}</span>
                  </div>
                  <div className="admin-health-ops-metric-row">
                    <span>Failed:</span>
                    <span>{opsMetrics.orphan_metrics?.links_failed_total ?? 0}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="admin-health-ops-error">No operations metrics available</div>
            )}
          </div>

          {/* Redis Health Section */}
          <div className="admin-health-ops-section">
            <h3>Redis Health</h3>
            {opsMetrics?.redis_health ? (
              <div className="admin-health-ops-card">
                <div className="admin-health-ops-metric-row">
                  <span>Status:</span>
                  <span className={`admin-health-ops-metric__value ${
                    opsMetrics.redis_health.status === "healthy" ? "success" : 
                    opsMetrics.redis_health.status === "unhealthy" ? "error" : "warning"
                  }`}>
                    {opsMetrics.redis_health.status.toUpperCase()}
                  </span>
                </div>
                <div className="admin-health-ops-metric-row">
                  <span>Circuit Breaker:</span>
                  <span className={`admin-health-ops-metric__value ${
                    opsMetrics.redis_health.circuit_breaker_state === "closed" ? "success" : 
                    opsMetrics.redis_health.circuit_breaker_state === "open" ? "error" : "warning"
                  }`}>
                    {opsMetrics.redis_health.circuit_breaker_state.toUpperCase()}
                  </span>
                </div>
                <div className="admin-health-ops-metric-row">
                  <span>Failures:</span>
                  <span className={`admin-health-ops-metric__value ${
                    opsMetrics.redis_health.circuit_breaker_failures > 0 ? "warning" : ""
                  }`}>
                    {opsMetrics.redis_health.circuit_breaker_failures}
                  </span>
                </div>
                {opsMetrics.redis_health.last_health_check && (
                  <div className="admin-health-ops-metric-row">
                    <span>Last Check:</span>
                    <span>{new Date(opsMetrics.redis_health.last_health_check * 1000).toLocaleString()}</span>
                  </div>
                )}
                {opsMetrics.redis_health.pool_stats && (
                  <div className="admin-health-ops-metric-row">
                    <span>Pool Stats:</span>
                    <span>
                      Redis Pools: {opsMetrics.redis_health.pool_stats.redis_pools ?? 0}, 
                      HTTP Sessions: {opsMetrics.redis_health.pool_stats.http_sessions ?? 0}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="admin-health-ops-error">Redis health data not available</div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

